<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main -->
  <main 
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
  >

  <!-- Navbar -->
  <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
    <HrTopbar
      :page-title="currentPageTitle"
      :is-sidebar-open="isSidebarOpen"
      :is-dark-mode="isDarkMode"
      @toggle-sidebar="toggleSidebar"
      @toggle-dark-mode="toggleDarkMode"
    />
  </header>

  <!-- Content -->
  <div class="p-6 mt-16">

    <!-- Top Bar -->
    <div class="flex justify-between items-center mb-8 p-4">

      <input
        v-model="search"
        type="text"
        placeholder="Search contract type..."
        class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72 focus:ring focus:ring-blue-300 dark:bg-gray-700 dark:text-white"
      />

      <button
        @click="openCreate"
        class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow"
      >
        + Add Contract Type
      </button>

    </div>

    <!-- Grid -->
    <div
      v-if="filteredContracts.length"
      class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
    >

      <div
        v-for="contract in filteredContracts"
        :key="contract.id"
        class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition"
      >

        <!-- Header -->
        <div class="flex items-center space-x-3 mb-4">

          <div class="w-12 h-12 rounded-full bg-secondary text-white flex items-center justify-center font-bold">
            {{ contract.code }}
          </div>

          <div>
            <h3 class="font-semibold text-gray-800 dark:text-white">
              {{ contract.name }}
            </h3>

            <span class="text-xs text-gray-500">
              {{ contract.duration }}
            </span>
          </div>

        </div>

        <!-- Description -->
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {{ contract.description }}
        </p>

        <!-- Actions -->
        <div class="flex justify-between text-sm">

          <button
            @click="editContract(contract)"
            class="text-blue-600 hover:underline"
          >
            Edit
          </button>

          <button
            @click="deleteContract(contract.id)"
            class="text-red-500 hover:underline"
          >
            Delete
          </button>

        </div>

      </div>

    </div>

    <!-- Empty -->
    <div
      v-else
      class="text-center text-gray-400 mt-20"
    >
      No contract types created yet
    </div>


    <!-- Modal -->
    <Transition name="modal">
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >

      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">

        <h3 class="text-lg font-semibold text-[#035a76] mb-6">
          {{ editing ? 'Edit Contract Type' : 'Create Contract Type' }}
        </h3>

        <form @submit.prevent="saveContract" class="space-y-4">

          <input
            v-model="form.name"
            type="text"
            placeholder="Contract Type Name"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <input
            v-model="form.code"
            type="text"
            placeholder="Code (PERM, INT, CNT...)"
            required
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <input
            v-model="form.duration"
            type="text"
            placeholder="Duration (e.g. Permanent, 1 Year, 6 Months)"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          />

          <textarea
            v-model="form.description"
            placeholder="Description"
            class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
          ></textarea>

          <div class="flex justify-end space-x-3 pt-2">

            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]"
            >
              Save
            </button>

          </div>

        </form>

      </div>

    </div>
    </Transition>

  </div>

  </main>
</div>
</template>

<script setup>

import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "@/components/hr/HrSidebar.vue"
import HrTopbar from "@/components/hr/HrTopbar.vue"

const currentPageTitle = ref("Contract Types")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)

const search = ref("")

const contracts = ref([])

// const contracts = ref([
// {
// id:1,
// name:"Permanent",
// code:"PERM",
// duration:"Permanent",
// description:"Full-time permanent employment"
// },
// {
// id:2,
// name:"Internship",
// code:"INT",
// duration:"6 Months",
// description:"Temporary internship contract"
// }
// ])

const fetchContractType = async () => {
  try{
    const response = await api.get("ContractType.php");
    contracts.value = response.data.map(contract => ({
      id:contract.id,
      name:contract.name,
      code:contract.code,
      description:contract.description,
      duration:contract.duration
    }));

  } catch (error) {
    console.error("Error fetching Departments:", error);
  }
};

const form = ref({
id:null,
name:"",
code:"",
duration:"",
description:""
})

const filteredContracts = computed(()=>{
return contracts.value.filter(c =>
c.name.toLowerCase().includes(search.value.toLowerCase())
)
})

const openCreate=()=>{
editing.value=false
form.value={id:null,name:"",code:"",duration:"",description:""}
showModal.value=true
}

const editContract=(contract)=>{
editing.value=true
form.value={...contract}
showModal.value=true
}

// const saveContract=()=>{

// if(editing.value){

// const index=contracts.value.findIndex(c=>c.id===form.value.id)
// contracts.value[index]={...form.value}

// }else{

// contracts.value.push({
// id:Date.now(),
// ...form.value
// })

// }

// closeModal()

// }

const saveContract = async () => {
  try{
    let response;
    if(editing.value){
      response = await api.put("ContractType.php", form.value);
      const index=contracts.value.findIndex(c=>c.id===form.value.id)
      if(index !== -1){
        contracts.value[index] = { ...form.value};
      }
        
  }else{
        response = await api.post("ContractType.php", form.value)
        contracts.value.push({
          id:response.data.id,
          name:response.data.name,
          code:response.data.code,
          description:response.data.description,
          duration:response.data.duration
        });
      }
closeModal();

  } catch (error) {
    console.error("Error saving contract type:", error);
  }
};

 
// const deleteContract=(id)=>{
// contracts.value=contracts.value.filter(c=>c.id!==id)
// }

const deleteContract = async (id) => {
try {

    await api.delete("ContractType.php", {
      data: { id: id }
    });

    contracts.value = contracts.value.filter(u => u.id !== id);

  } catch (error) {
    console.error("Error deleting department:", error);
  }
}

const closeModal=()=>{
showModal.value=false
}

const toggleSidebar=()=>{
isSidebarOpen.value=!isSidebarOpen.value
}

const toggleDarkMode=()=>{
isDarkMode.value=!isDarkMode.value

if(isDarkMode.value){
document.documentElement.classList.add("dark")
}else{
document.documentElement.classList.remove("dark")
}

}
onMounted(() => {
  fetchContractType();
}) 

</script>

<style>

.modal-enter-active,
.modal-leave-active{
transition:all .25s ease;
}

.modal-enter-from{
opacity:0;
transform:scale(.9);
}

.modal-leave-to{
opacity:0;
transform:scale(.9);
}

</style>